import { GET_LOOTBOXES_GAMES_COUNT } from '@/api/query'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { SimpleGrid, Flex, Button, Skeleton, Text, Box } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import { ProgressBar } from '../atoms/ProgressBar'
import {
  LootboxType,
  LootboxMetadata,
  LootboxLimited,
  LootboxState,
  RiskLevelEnum,
  LootboxV2,
} from '../templates/transformLootboxRewards'
import Image from 'next/image'
import { useQuery } from '@apollo/client'
import { LootboxTags } from '../molecules/LootboxTags/LootboxTags'
import LockIcon from '@/public/assets/icons/lock.svg'
import { useLootboxContext } from '@/context/LootboxProvider'
import { useBalanceStore } from '@/context/UserBalance'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { useTranslation } from 'next-i18next/pages'

export const Collections = () => {
  const { t } = useTranslation()
  return (
    <>
      <Text mb={4} fontSize={'3xl'} fontWeight={700}>
        {t('common.box-collections')}
      </Text>
      <MarketList />
    </>
  )
}

const MarketList = () => {
  const denom = useBalanceStore((state) => state.denom)
  const { getLootboxesList } = useLootboxContext()
  const lootboxes = getLootboxesList()

  return (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} columnGap={6} rowGap={8}>
      {lootboxes
        .filter((x: any) => x.collections || x.metadata?.collections)
        .map((lootbox, index) => {
          if (lootbox.type === LootboxType.LIMITED) {
            return <LimitedLootboxCard key={index} lootbox={lootbox as LootboxLimited & LootboxMetadata} />
          }
          if (lootbox.type === LootboxType.UNLIMITED) {
            return <Unlimited2LootboxCard key={index} denom={denom} lootbox={lootbox as LootboxV2} />
          }

          return null
        })}
    </SimpleGrid>
  )
}

export const Unlimited2LootboxCard = ({
  lootbox,
  tags,
  denom,
}: {
  lootbox: LootboxV2
  denom: string
  tags?: React.ReactElement
}) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { getLootboxImagePrefix } = useLootboxContext()

  return (
    <Box rounded={'2xl'} bg={'ninja.600'}>
      <Flex
        pos={'relative'}
        onClick={() => router.push('/lootbox/' + lootbox.slug)}
        cursor={'pointer'}
        rounded={'2xl'}
        justifyContent={'center'}
        alignItems={'center'}
        h={'350px'}
        boxShadow={`0px -12px 140px -24px ${lootbox.metadata.shadowColor} inset, 0px -40px 148px -42px ${lootbox.metadata.shadowColor} inset;`}
        sx={{
          '&:hover img': {
            transform: 'scale(1.2)',
          },
          '& img': {
            transition: '.1s',
          },
        }}
      >
        <Flex top={5} justifyContent={'space-between'} alignItems={'center'} left={5} right={5} pos={'absolute'}>
          <LootboxTags tags={lootbox.metadata.tags} />
          {tags ? (
            tags
          ) : (
            <Box
              rounded={'lg'}
              fontSize={'sm'}
              fontWeight={700}
              border={'1px solid'}
              py={0.5}
              px={2}
              borderColor={'whiteAlpha.300'}
              h={7}
            >
              {lootbox.metadata.maxMultiplier}x
            </Box>
          )}
        </Flex>
        <Box w={200}>
          <Image
            width={516}
            height={497}
            src={getLootboxImagePrefix(lootbox.slug) + (lootbox.image.src || 'lootbox.png')}
            alt=""
          />
        </Box>
      </Flex>
      <Box p={5}>
        <Text fontSize={'2xl'} fontWeight={700} mb={2}>
          {lootbox.metadata.title}
        </Text>
        <RiskLevel level={lootbox.metadata.risk} />
        <Flex mt={3} justifyContent={'space-between'}>
          <FiatOrCryptoAmount
            wrapperStyles={{
              fontSize: 'xl',
            }}
            iconSize={5}
            value={new TokenMoney(lootbox.price[denom], denom)}
          />
          <Button onClick={() => router.push('/lootbox/' + lootbox.slug)} rounded={'2xl'} h={'52px'} px={10}>
            {t('common.view')}
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

const RiskLevel = ({ level }: { level: RiskLevelEnum }) => {
  const { t } = useTranslation()
  const levels = [RiskLevelEnum.LOW, RiskLevelEnum.MEDIUM, RiskLevelEnum.HIGH]
  const riskMap = {
    [RiskLevelEnum.LOW]: t('common.low'),
    [RiskLevelEnum.MEDIUM]: t('common.medium'),
    [RiskLevelEnum.HIGH]: t('common.high'),
  }
  return (
    <>
      <Box bg={'transparent'} rounded={'xl'} w={'full'} minW={'280px'}>
        <Flex fontSize={'sm'} fontWeight={700} mb={1} justifyContent={'space-between'}>
          <Text color={'whiteAlpha.600'}>{t('common.risk-level')}</Text>
          <Text>{riskMap[level]}</Text>
        </Flex>
        <SimpleGrid h={3} w={'full'} columns={3} gap={1}>
          {levels.map((risk, index) => (
            <Box
              key={index}
              h={'full'}
              bg={level === risk ? colors[risk].active.bg : colors[risk].unactive.bg}
              roundedLeft={index === 0 ? 'full' : 0.5}
              roundedRight={index === levels.length - 1 ? 'full' : 0.5}
              boxShadow={level === risk ? colors[risk].active.boxShadow : colors[risk].unactive.boxShadow}
            />
          ))}
        </SimpleGrid>
      </Box>
    </>
  )
}

const colors = {
  [RiskLevelEnum.LOW]: {
    active: {
      bg: 'rgba(120, 255, 98, 0.48);',
      boxShadow:
        '0px 0px 3px 0px rgba(255, 255, 255, 0.80) inset, 0px 0px 8px 0px #78FF62 inset, 0px 0px 18.9px 0px #78FF62 inset, 0px 0px 12px 0px rgba(120, 255, 98, 0.80);',
    },
    unactive: {
      bg: 'rgba(120, 255, 98, 0.12);',
      boxShadow: '0px 0px 3px 0px rgba(255, 255, 255, 0.12) inset;',
    },
  },
  [RiskLevelEnum.MEDIUM]: {
    active: {
      bg: 'rgba(255, 139, 55, 0.48);',
      boxShadow:
        '0px 0px 3px 0px rgba(255, 255, 255, 0.80) inset, 0px 0px 8px 0px #FF8B377A inset, 0px 0px 18.9px 0px #FF8B377A inset, 0px 0px 12px 0px rgba(255, 139, 55, 0.80);',
    },
    unactive: {
      bg: 'rgba(255, 139, 55, 0.12);',
      boxShadow: '0px 0px 3px 0px rgba(255, 255, 255, 0.12) inset;',
    },
  },
  [RiskLevelEnum.HIGH]: {
    active: {
      bg: 'rgba(255, 10, 83, 0.48);',
      boxShadow:
        '0px 0px 3px 0px rgba(255, 255, 255, 0.80) inset, 0px 0px 8px 0px #FF0A53 inset, 0px 0px 18.9px 0px #FF0A53 inset, 0px 0px 12px 0px rgba(255, 10, 83, 0.80);',
    },
    unactive: {
      bg: 'rgba(255, 10, 83, 0.12);',
      boxShadow: '0px 0px 3px 0px rgba(255, 255, 255, 0.12) inset;',
    },
  },
}

export const LimitedLootboxCard = ({ lootbox }: { lootbox: LootboxLimited & LootboxMetadata }) => {
  const { t } = useTranslation()
  const { getLootboxImagePrefix } = useLootboxContext()
  const router = useRouter()
  // const { data, loading, error } = useQuery(GET_LOOTBOX_SALES, {
  //   variables: {
  //     id: lootbox.limited.contracts.sale,
  //   },
  // })

  const { data, loading, error } = useQuery(GET_LOOTBOXES_GAMES_COUNT, {})

  if (error) {
    return null
  }

  if (loading) {
    return <Skeleton h={'535px'} rounded={'2xl'} startColor={'ninja.500'} endColor={'ninja.600'} />
  }

  const lootboxStata = Number(
    data?.lootboxes?.edges?.find((x) => x.node?.id == lootbox.contract)?.node?.gameCount || '0'
  )

  return (
    <Box rounded={'2xl'} bg={'ninja.600'}>
      <Flex
        onClick={() => router.push('/lootbox/' + lootbox.contract)}
        cursor={'pointer'}
        rounded={'2xl'}
        justifyContent={'center'}
        alignItems={'center'}
        h={'350px'}
        pos={'relative'}
        boxShadow={`0px 0px 15.4px 0px ${lootbox.image.shadowColor} inset, 0px 0px 15.4px 0px ${lootbox.image.shadowColor} inset, 0px -67px 100.3px 0px ${lootbox.image.shadowColor} inset;`}
        sx={{
          '&:hover img': {
            transform: 'scale(1.2)',
          },
          '& img': {
            transition: '.1s',
          },
        }}
      >
        <Flex pos={'absolute'} top={5} left={5} right={5} justifyContent={'space-between'} alignItems={'center'}>
          <LootboxTags tags={lootbox.tags} />
          <Flex alignItems={'center'} gap={2}>
            {lootbox.maxMultiplier && (
              <Box
                rounded={'lg'}
                fontSize={'sm'}
                fontWeight={700}
                border={'1px solid'}
                py={0.5}
                px={2}
                borderColor={'whiteAlpha.300'}
                h={7}
              >
                {lootbox.maxMultiplier}x
              </Box>
            )}
            {lootbox.state !== LootboxState.ACTIVE && (
              <Box boxSize={7} p={1.5} border={'1px solid'} borderColor={'whiteAlpha.300'} rounded={'lg'}>
                <LockIcon />
              </Box>
            )}
          </Flex>
        </Flex>
        <Box w={200}>
          <Image
            width={516}
            height={497}
            src={getLootboxImagePrefix(lootbox.contract) + (lootbox.image.src || 'lootbox.png')}
            alt=""
          />
        </Box>
      </Flex>
      <Box p={5}>
        <Text fontSize={'2xl'} fontWeight={700} mb={2}>
          {lootbox.title}
        </Text>
        <ProgressBar
          title={lootbox.state === LootboxState.SALE ? t('lootbox.available-limit') : t('lootbox.remaining-supply')}
          //   totalCount={lootbox.state === LootboxState.SALE ? 0 : data?.lootboxes?.salesCount || 0}
          totalCount={lootboxStata}
          totalLimit={lootbox.limited.quantity}
        />
        <Flex mt={3} flexDirection={'row-reverse'} justifyContent={'space-between'}>
          <Button onClick={() => router.push('/lootbox/' + lootbox.contract)} rounded={'2xl'} h={'52px'} px={10}>
            {t('common.view')}
          </Button>
          {/* {lootbox.limited.sale && (
            <Amount size="xl" value={formatAmount(lootbox.limited.sale.price.amount, { precision: 2 })} />
          )} */}
        </Flex>
      </Box>
    </Box>
  )
}
